import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useAuditLogById } from '@/hooks/useAuditLogById'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { format } from 'date-fns'
import { ScrollArea } from '@/components/ui/scroll-area'

interface AuditLogDialogProps {
  logId: string
  open: boolean
  onClose: () => void
}

const ACTION_COLORS: Record<string, string> = {
  CREATE: 'bg-green-100 text-green-800',
  READ: 'bg-blue-100 text-blue-800',
  UPDATE: 'bg-yellow-100 text-yellow-800',
  DELETE: 'bg-red-100 text-red-800',
}

const ACTION_LABELS: Record<string, string> = {
  CREATE: '新增',
  READ: '查詢',
  UPDATE: '更新',
  DELETE: '刪除',
}

export function AuditLogDialog({ logId, open, onClose }: AuditLogDialogProps) {
  const { log, isLoading, isError } = useAuditLogById(logId)

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>操作日誌詳情</DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(80vh-100px)]">
          {isLoading && (
            <div className="space-y-4">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-20 w-full" />
            </div>
          )}

          {isError && (
            <div className="text-center text-destructive py-8">載入日誌詳情失敗。請稍後再試。</div>
          )}

          {log && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">日誌 ID</label>
                  <div className="mt-1 font-mono text-sm">{log.log_id}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">時間</label>
                  <div className="mt-1">
                    {format(new Date(log.created_at), 'yyyy-MM-dd HH:mm:ss')}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">操作類型</label>
                  <div className="mt-1">
                    <Badge className={ACTION_COLORS[log.action] || 'bg-gray-100 text-gray-800'}>
                      {ACTION_LABELS[log.action] || log.action}
                    </Badge>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">HTTP 狀態</label>
                  <div className="mt-1">
                    <Badge
                      variant={
                        log.response_status && log.response_status >= 200 && log.response_status < 300
                          ? 'default'
                          : 'destructive'
                      }
                    >
                      {log.response_status || 'N/A'}
                    </Badge>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Resource Info */}
              <div>
                <h3 className="font-semibold mb-3">資源資訊</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">資源類型</label>
                    <div className="mt-1">{log.resource_type}</div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">資源 ID</label>
                    <div className="mt-1 font-mono text-sm">{log.resource_id || '-'}</div>
                  </div>
                </div>
              </div>

              <Separator />

              {/* User Info */}
              <div>
                <h3 className="font-semibold mb-3">使用者資訊</h3>
                {log.user ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">使用者名稱</label>
                      <div className="mt-1">{log.user.user_name}</div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">帳號</label>
                      <div className="mt-1">{log.user.account}</div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Email</label>
                      <div className="mt-1">{log.user.email}</div>
                    </div>
                    {log.user.user_role && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">角色</label>
                        <div className="mt-1">{log.user.user_role.role_name}</div>
                      </div>
                    )}
                  </div>
                ) : log.account ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">使用者名稱</label>
                      <div className="mt-1 text-muted-foreground italic">已刪除帳號</div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">帳號</label>
                      <div className="mt-1">{log.account}</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-muted-foreground">系統操作</div>
                )}
              </div>

              <Separator />

              {/* Request Info */}
              <div>
                <h3 className="font-semibold mb-3">請求資訊</h3>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">端點</label>
                    <div className="mt-1 font-mono text-sm">
                      <span className="text-muted-foreground">{log.http_method}</span> {log.endpoint}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">IP 位址</label>
                    <div className="mt-1 font-mono text-sm">{log.ip_address}</div>
                  </div>
                  {log.user_agent && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">User Agent</label>
                      <div className="mt-1 text-sm break-all">{log.user_agent}</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Request Body */}
              {log.request_body && (
                <>
                  <Separator />
                  <div>
                    <h3 className="font-semibold mb-3">請求內容</h3>
                    <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto">
                      {JSON.stringify(log.request_body, null, 2)}
                    </pre>
                  </div>
                </>
              )}

              {/* Data Changes */}
              {(log.old_data || log.new_data) && (
                <>
                  <Separator />
                  <div>
                    <h3 className="font-semibold mb-3">資料變更</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {log.old_data && (
                        <div>
                          <label className="text-sm font-medium text-muted-foreground mb-2 block">
                            變更前
                          </label>
                          <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto">
                            {JSON.stringify(log.old_data, null, 2)}
                          </pre>
                        </div>
                      )}
                      {log.new_data && (
                        <div>
                          <label className="text-sm font-medium text-muted-foreground mb-2 block">
                            變更後
                          </label>
                          <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto">
                            {JSON.stringify(log.new_data, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Diff */}
              {log.diff && (
                <>
                  <Separator />
                  <div>
                    <h3 className="font-semibold mb-3">變更差異</h3>
                    <pre className="bg-muted p-4 rounded-lg text-xs overflow-x-auto">
                      {JSON.stringify(log.diff, null, 2)}
                    </pre>
                  </div>
                </>
              )}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  )
}
